import { App as AntdApp, ConfigProvider as AntdConfigProvider } from 'antd';
import { CheckCircleBoldIcon, InfoCircleBoldIcon, DangerTriangleBoldIcon, CloseCircleBoldIcon, AltArrowDownLinearIcon } from '@solar-icons/react';
import { MessageBridge } from '../Message';
import { NotificationBridge } from '../Notification';

const ALERT_CONFIG = {
    successIcon: <CheckCircleBoldIcon color="var(--color-feedback-success-text)" />,
    infoIcon: <InfoCircleBoldIcon color="var(--color-primary-700)" />,
    warningIcon: <DangerTriangleBoldIcon color="var(--color-feedback-warning-text)" />,
    errorIcon: <CloseCircleBoldIcon color="var(--color-feedback-error-text)" />,
};

const SELECT_CONFIG = {
    suffixIcon: <AltArrowDownLinearIcon size={14} color="var(--color-text-tertiary)" />,
};

const MESSAGE_CONFIG = {
    styles: {
        root: { maxWidth: 400 },
        title: { fontSize: 16, fontWeight: 500 },
    },
};

const KEYBOARD_TRIGGER = ['hover', 'focus'];

const BASE_THEME = {
    cssVar: { key: 'ant' },
    hashed: false,
    token: {
        fontFamily: "'Hanken Grotesk', 'Open Sans', sans-serif",
        colorPrimary: '#4D919F',
        borderRadius: 6,
        colorTextPlaceholder: 'var(--color-neutral-550)',
        controlOutlineWidth: 1,
        controlOutline: 'var(--color-focus-outline)',
    },
    components: {
        Button: {
            defaultColor: 'var(--color-surface-brand)',
            colorPrimary: 'var(--color-primary-700)',
            defaultBg: 'var(--color-surface-brand-subtle)',
            defaultHoverBg: 'var(--color-primary-200)',
            defaultActiveBg: 'var(--color-primary-300)',
            defaultBorderColor: 'var(--color-surface-brand-subtle)',
            defaultHoverBorderColor: 'var(--color-primary-200)',
            defaultActiveBorderColor: 'var(--color-primary-300)',
            defaultHoverColor: 'var(--color-surface-brand)',
            defaultActiveColor: 'var(--color-surface-brand)',
        },
        Progress: {
            defaultColor: 'var(--color-primary-500)',
        },
        Message: {
            contentPadding: '14px 16px',
        },
        Breadcrumb: {
            itemColor: 'var(--color-text-tertiary)',
            linkColor: 'var(--color-text-tertiary)',
            linkHoverColor: 'var(--color-text-secondary)',
            lastItemColor: 'var(--color-text-secondary)',
            separatorColor: 'var(--color-text-tertiary)',
        },
        Alert: {
            colorSuccess: 'var(--color-feedback-success-text)',
            colorSuccessBg: 'var(--color-feedback-success-bg)',
            colorSuccessBorder: 'var(--color-feedback-success-text)',
            colorError: 'var(--color-feedback-error-text)',
            colorErrorBg: 'var(--color-feedback-error-bg)',
            colorErrorBorder: 'var(--color-feedback-error-text)',
            colorInfo: 'var(--color-primary-700)',
            colorInfoBg: 'var(--color-primary-100)',
            colorInfoBorder: 'var(--color-primary-700)',
            colorWarning: 'var(--color-feedback-warning-text)',
            colorWarningBg: 'var(--color-feedback-warning-bg)',
            colorWarningBorder: 'var(--color-feedback-warning-text)',
        },
        Dropdown: {
            borderRadiusLG: 10,
        },
        Segmented: {
            trackBg: 'var(--color-neutral-100)',
            itemColor: 'var(--color-text-secondary)',
            itemHoverColor: 'var(--color-text-primary)',
            itemHoverBg: 'var(--color-neutral-200)',
            itemSelectedBg: 'var(--color-primary-700)',
            itemSelectedColor: '#FFFFFF',
        },
    },
};

/**
 * @param {Object} props
 * @param {Object} [props.theme] - antd `ConfigProvider` theme shape, merged with vector's own base theme
 *   (`BASE_THEME`); a caller's `token`/`components` values win over the defaults. See
 *   `vector/NOTES.md` → Theming.
 */
const ConfigProvider = ({ theme = {}, children }) => {
    const mergedTheme = {
        ...BASE_THEME,
        ...theme,
        token: { ...BASE_THEME.token, ...theme?.token },
        components: {
            ...BASE_THEME.components,
            ...theme?.components,
            Button: { ...BASE_THEME.components.Button, ...theme?.components?.Button },
            Progress: { ...BASE_THEME.components.Progress, ...theme?.components?.Progress },
            Alert: { ...BASE_THEME.components.Alert, ...theme?.components?.Alert },
            Breadcrumb: { ...BASE_THEME.components.Breadcrumb, ...theme?.components?.Breadcrumb },
            Message: { ...BASE_THEME.components.Message, ...theme?.components?.Message },
            Dropdown: { ...BASE_THEME.components.Dropdown, ...theme?.components?.Dropdown },
            Segmented: { ...BASE_THEME.components.Segmented, ...theme?.components?.Segmented },
        },
    };

    return (
        <AntdConfigProvider
            theme={mergedTheme}
            alert={ALERT_CONFIG}
            select={SELECT_CONFIG}
            message={MESSAGE_CONFIG}
            tooltip={{ trigger: KEYBOARD_TRIGGER }}
            popover={{ trigger: KEYBOARD_TRIGGER }}
            popconfirm={{ trigger: KEYBOARD_TRIGGER }}
        >
            {/* component=false avoids an extra div; App only exists to mount message/notification/modal context holders inside the themed subtree. */}
            <AntdApp component={false} message={{ duration: 5, maxCount: 3 }}>
                <MessageBridge />
                <NotificationBridge />
                {children}
            </AntdApp>
        </AntdConfigProvider>
    );
};

export default ConfigProvider;
